import { apiClient } from './client';
import { getDeviceId } from '@/lib/device';
import type { AuthUser } from '@/stores/auth-store';
import type { BaseUser, UserRole } from '@comp-sys/shared';

// Auth API types
export interface LoginRequest {
  phoneNumber?: string;
  countryCode?: string;
  email?: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: BaseUser;
}

export interface RegisterRequest {
  phoneNumber?: string;
  countryCode?: string;
  email?: string;
  password: string;
  role?: UserRole;
  inviteCode?: string;
  locale?: string;
  verificationCode?: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
  deviceId: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

export interface SendCodeRequest {
  target: string;
  type: 'phone' | 'email';
  purpose: 'register' | 'login' | 'reset-password';
}

export interface SendCodeResponse {
  success: boolean;
  message: string;
}

export interface LoginWithCodeRequest {
  phoneNumber: string;
  countryCode: string;
  verificationCode: string;
  rememberMe?: boolean;
}

// Backend returns req.user directly, not wrapped
export type MeResponse = BaseUser;

/**
 * Map a BaseUser (from any auth response) to the AuthUser shape stored in the
 * auth store. Backfills `roles`/`activeRole` for older backends that only
 * returned `role`.
 */
export function toAuthUser(user: BaseUser): AuthUser {
  return {
    id: user.id,
    phoneNumber: user.phoneNumber ?? null,
    countryCode: user.countryCode ?? null,
    email: user.email ?? null,
    displayName: user.displayName ?? null,
    role: user.role,
    roles: user.roles ?? [user.role],
    activeRole: user.activeRole ?? user.role,
    schoolId: user.schoolId ?? null,
    institutionId: user.institutionId ?? null,
    hasCompletedOnboarding: user.hasCompletedOnboarding,
  };
}

export interface SwitchRoleResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

export interface PublicKeyResponse {
  publicKey: string;
  algorithm: string;
  keyLength: number;
}

export interface GenerateMagicLinkRequest {
  userId: string;
  purpose: 'IMPERSONATE' | 'MAGIC_LINK';
  expiresIn: number;
}

export interface GenerateMagicLinkResponse {
  token: string;
  expiresAt: string;
}

export interface VerifyMagicLinkResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: BaseUser;
  impersonator?: {
    userId: string;
  };
}

export interface ExitImpersonateResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  user: BaseUser;
}

// Auth API functions
export const authApi = {
  login: (data: LoginRequest) =>
    apiClient.post<LoginResponse>('auth/login', {
      ...data,
      deviceId: getDeviceId(),
    }),

  register: (data: RegisterRequest) =>
    apiClient.post<LoginResponse>('auth/register', data),

  refresh: (data: RefreshTokenRequest) =>
    apiClient.post<RefreshTokenResponse>('auth/refresh', data),

  logout: () =>
    apiClient.post<void>('auth/logout', { deviceId: getDeviceId() }),

  me: () =>
    apiClient.post<MeResponse>('auth/me', {}),

  sendCode: (data: SendCodeRequest) =>
    apiClient.post<SendCodeResponse>('auth/send-code', data),

  loginWithCode: (data: LoginWithCodeRequest) =>
    apiClient.post<LoginResponse>('auth/login-with-code', {
      ...data,
      deviceId: getDeviceId(),
    }),

  getPublicKey: () =>
    apiClient.get<PublicKeyResponse>('auth/public-key'),

  generateMagicLink: (data: GenerateMagicLinkRequest) =>
    apiClient.post<GenerateMagicLinkResponse>('auth/magic-link/generate', data),

  verifyMagicLink: (token: string, sessionRefreshToken?: string) =>
    apiClient.post<VerifyMagicLinkResponse>('auth/magic-link/verify', { token, sessionRefreshToken }),

  exitImpersonate: (sessionRefreshToken: string) =>
    apiClient.post<ExitImpersonateResponse>('auth/impersonate/exit', { sessionRefreshToken }),

  switchRole: (role: UserRole) =>
    apiClient.post<SwitchRoleResponse>('auth/switch-role', { role }),
};
